/**
 * EG Hero GSAP — entrance + floating badges. Cleans up with gsap.context.
 */
(function () {
  const SELECTOR = '[data-eg-hero-overlay]';

  const parseBool = (value) => value === 'true' || value === true;

  const revealAnimatedNodes = (root) => {
    for (const el of root.querySelectorAll('.eg-animate')) {
      el.style.opacity = '1';
      el.style.transform = 'none';
    }
  };

  const initHero = (root) => {
    if (!root || root.dataset.egHeroGsapReady === 'true') return;

    const gsap = window.gsap;
    if (!gsap) {
      revealAnimatedNodes(root);
      return;
    }

    if (window.ScrollTrigger) {
      gsap.registerPlugin(window.ScrollTrigger);
    }

    root.dataset.egHeroGsapReady = 'true';
    const abortCtl = new AbortController();
    root._egHeroGsapAbort = abortCtl;

    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const enableEntrance = parseBool(root.dataset.animateEntrance) && !prefersReduced;
    const enableFloat = parseBool(root.dataset.animateFloat) && !prefersReduced;

    const ctx = gsap.context(() => {
      const animated = root.querySelectorAll('.eg-animate');

      if (enableEntrance) {
        for (const el of animated) {
          const delay = parseFloat(el.dataset.delay || '0') || 0;
          gsap.from(el, {
            opacity: 0,
            y: 40,
            duration: 1,
            delay,
            ease: 'power3.out',
            onComplete: () => {
              if (!el.classList.contains('eg-float')) {
                gsap.set(el, { clearProps: 'transform' });
              }
            },
          });
        }
      } else {
        gsap.set(animated, { opacity: 1, y: 0 });
      }

      const floats = gsap.utils.toArray(root.querySelectorAll('.eg-float'));

      floats.forEach((floatRoot, i) => {
        const inner = floatRoot.querySelector('.eg-float__inner');
        if (!inner) return;

        if (enableFloat) {
          const yMove = 8 + i * 2;
          const duration = 2 + i * 0.3;

          gsap.to(floatRoot, {
            y: -yMove,
            duration,
            repeat: -1,
            yoyo: true,
            ease: 'sine.inOut',
            delay: i * 0.2,
          });
        }

        if (!prefersReduced) {
          const onEnter = () => {
            gsap.to(inner, { scale: 1.08, duration: 0.2, overwrite: 'auto' });
          };

          const onLeave = () => {
            gsap.to(inner, { scale: 1, duration: 0.2, overwrite: 'auto' });
          };

          floatRoot.addEventListener('mouseenter', onEnter, { signal: abortCtl.signal });
          floatRoot.addEventListener('mouseleave', onLeave, { signal: abortCtl.signal });
        }
      });
    }, root);

    root._egHeroGsapCtx = ctx;
  };

  const destroyHero = (root) => {
    if (!root) return;
    root._egHeroGsapAbort?.abort();
    delete root._egHeroGsapAbort;
    if (root._egHeroGsapCtx) {
      root._egHeroGsapCtx.revert();
      delete root._egHeroGsapCtx;
    }
    delete root.dataset.egHeroGsapReady;
  };

  const onReady = () => {
    for (const root of document.querySelectorAll(SELECTOR)) {
      initHero(root);
    }
  };

  const fallbackIfGsapNeverLoads = () => {
    if (window.gsap) return;
    for (const root of document.querySelectorAll(SELECTOR)) {
      revealAnimatedNodes(root);
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', onReady);
  } else {
    onReady();
  }

  window.addEventListener('load', () => {
    fallbackIfGsapNeverLoads();
  });

  document.addEventListener('shopify:section:load', (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;
    const root = target.matches(SELECTOR) ? target : target.querySelector(SELECTOR);
    if (root) {
      destroyHero(root);
      initHero(root);
    }
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;
    const root = target.matches(SELECTOR) ? target : target.querySelector(SELECTOR);
    if (root) destroyHero(root);
  });
})();
